import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { radialLayout, type EntityKind, type GraphNode, type HuntGraph, type LayoutPoint } from '../../lib/hunt-graph'

/**
 * The pivot graph canvas (E5) — inline SVG, house precedent (TimelineChart):
 * viewBox pan/zoom, token colors, no chart library. Layout is the pure
 * radialLayout; this component owns only pixels and gestures.
 *
 * Keyboard: every node is focusable (Tab walks them in id order) and Enter
 * selects — the mini-summary and its actions live with the HOST, which gets
 * the selection through onSelect.
 *
 * The Signals storyline graph is not drawn here: it lives on a force-graph
 * canvas in `components/signal/force`. So this canvas draws only what
 * Hunt and the DeepPanel pivot graph build — kind-coloured circles,
 * undirected edges; a GraphNode's storyline fields (tone, shape, badge,
 * title) and a directed edge are ignored here.
 *
 * Performance: edges and nodes are two memoized layers whose props never move
 * with a pan (view.x / y) — a pan re-renders the svg shell only. Pan updates
 * are coalesced into one view change per frame.
 */

/** Categorical slot per entity kind — tokens only. */
const KIND_COLOR: Record<EntityKind, string> = {
  user: 'var(--viz-1)',
  host: 'var(--viz-2)',
  ip: 'var(--viz-3)',
  domain: 'var(--viz-4)',
  hash: 'var(--viz-5)',
  other: 'var(--viz-other)',
}

const SIZE = 1000

const clipLabel = (v: string) => (v.length > 22 ? `${v.slice(0, 21)}…` : v)

type View = { x: number; y: number; w: number; h: number }
const FULL: View = { x: 0, y: 0, w: SIZE, h: SIZE }

/** Zoom `v` by `factor` around its centre (width clamped to [minW, maxW]). */
function zoomView(v: View, factor: number, minW: number, maxW: number): View {
  const w = Math.min(maxW, Math.max(minW, v.w * factor))
  return { x: v.x + (v.w - w) * 0.5, y: v.y + (v.w - w) * 0.5, w, h: w }
}

export function GraphCanvas({
  graph,
  selected,
  onSelect,
}: {
  graph: HuntGraph
  selected: string | null
  onSelect: (id: string | null) => void
}) {
  const points = useMemo(() => radialLayout(graph, SIZE), [graph])
  // Pan/zoom as viewBox state; drag pans, the buttons zoom.
  const [view, setView] = useState(FULL)
  const drag = useRef<{ px: number; py: number; vx: number; vy: number } | null>(null)
  const svgRef = useRef<SVGSVGElement>(null)

  const scale = () => {
    const r = svgRef.current?.getBoundingClientRect()
    return r && r.width > 0 ? view.w / r.width : 1
  }

  // Pan: one view change per animation frame, whatever the event rate.
  const pending = useRef<((v: View) => View)[]>([])
  const frame = useRef(0)
  const queueView = useCallback((f: (v: View) => View) => {
    if (typeof requestAnimationFrame !== 'function') return setView(f)
    pending.current.push(f)
    if (frame.current) return
    frame.current = requestAnimationFrame(() => {
      frame.current = 0
      const fs = pending.current
      pending.current = []
      setView((v) => fs.reduce((acc, fn) => fn(acc), v))
    })
  }, [])
  useEffect(
    () => () => {
      if (frame.current) cancelAnimationFrame(frame.current)
    },
    [],
  )

  const minW = SIZE / 8
  const maxW = SIZE * 4
  const zoom = (factor: number) => setView((v) => zoomView(v, factor, minW, maxW))
  const strokeK = view.w / SIZE

  if (!graph.nodes.length) return null

  return (
    <div className="hud-stack" style={{ gap: 'var(--bloo-space-1)' }}>
      <svg
        ref={svgRef}
        viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`}
        style={{ width: '100%', aspectRatio: '1 / 1', touchAction: 'none' }}
        role="application"
        aria-label="Pivot graph — drag to pan; nodes are focusable, Enter selects"
        onMouseDown={(e) => {
          if ((e.target as Element).closest('[data-node]')) return
          drag.current = { px: e.clientX, py: e.clientY, vx: view.x, vy: view.y }
        }}
        onMouseMove={(e) => {
          const d = drag.current
          if (!d) return
          const k = scale()
          const x = d.vx - (e.clientX - d.px) * k
          const y = d.vy - (e.clientY - d.py) * k
          queueView((v) => ({ ...v, x, y }))
        }}
        onMouseUp={() => (drag.current = null)}
        onMouseLeave={() => {
          drag.current = null
        }}
      >
        <Edges graph={graph} points={points} strokeK={strokeK} />
        <Nodes graph={graph} points={points} selected={selected} onSelect={onSelect} />
      </svg>
      <div className="hud-row" style={{ gap: 'var(--bloo-space-1)', justifyContent: 'space-between' }}>
        <ul className="agg-legend" aria-label="Entity kinds" style={{ margin: 0 }}>
          {(Object.keys(KIND_COLOR) as EntityKind[])
            .filter((k) => graph.nodes.some((n) => n.kind === k))
            .map((k) => (
              <li key={k} className="agg-legend-item">
                <span className="agg-swatch" style={{ background: KIND_COLOR[k] }} />
                <span className="agg-legend-v mono">{k}</span>
              </li>
            ))}
        </ul>
        <span className="hud-row" style={{ gap: 'var(--bloo-space-1)' }}>
          <button type="button" className="list-chip" aria-label="Zoom in" onClick={() => zoom(0.8)}>
            +
          </button>
          <button type="button" className="list-chip" aria-label="Zoom out" onClick={() => zoom(1.25)}>
            −
          </button>
          <button
            type="button"
            className="list-chip"
            aria-label="Reset view"
            onClick={() => {
              pending.current = []
              setView(FULL)
            }}
          >
            fit
          </button>
        </span>
      </div>
    </div>
  )
}

interface NodesProps {
  graph: HuntGraph
  points: ReadonlyMap<string, LayoutPoint>
  selected: string | null
  onSelect: (id: string | null) => void
}

/** Nodes (memoized: none of its props move with a pan or a zoom). */
const Nodes = memo(function Nodes({ graph, points, selected, onSelect }: NodesProps) {
  const activate = (id: string) => onSelect(selected === id ? null : id)
  return (
    <>
      {graph.nodes.map((n: GraphNode) => {
        const p = points.get(n.id)
        if (!p) return null
        const r = n.expanded ? 16 : 11
        const isSel = selected === n.id
        return (
          <g
            key={n.id}
            data-node={n.id}
            transform={`translate(${p.x} ${p.y})`}
            role="button"
            tabIndex={0}
            aria-label={`${n.kind} ${n.value}${n.expanded ? ', expanded' : ''}`}
            aria-pressed={isSel}
            style={{ cursor: 'pointer', outline: 'none' }}
            onClick={() => activate(n.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                activate(n.id)
              }
            }}
          >
            <circle
              r={r}
              fill={KIND_COLOR[n.kind]}
              stroke={isSel ? 'var(--bui-accent, currentColor)' : 'var(--bui-surface, transparent)'}
              strokeWidth={isSel ? 4 : 1.5}
            />
            <title>{`${n.kind} · ${n.value}${n.summary ? ` · ${n.summary.hits.toLocaleString('en-US')} hits` : ''}`}</title>
            <text y={r + 14} textAnchor="middle" className="mono" style={{ fontSize: 12, fill: 'var(--bui-text-muted, currentColor)' }}>
              {clipLabel(n.value)}
            </text>
          </g>
        )
      })}
    </>
  )
})

interface EdgesProps {
  graph: HuntGraph
  points: ReadonlyMap<string, LayoutPoint>
  strokeK: number
}

/** Edges, under the nodes (memoized: a pan re-renders none). */
const Edges = memo(function Edges({ graph, points, strokeK }: EdgesProps) {
  return (
    <>
      {graph.edges.map((e) => {
        const a = points.get(e.from)
        const b = points.get(e.to)
        if (!a || !b) return null
        return (
          <line
            key={`${e.from}->${e.to}`}
            x1={a.x}
            y1={a.y}
            x2={b.x}
            y2={b.y}
            stroke="var(--bui-border-strong, currentColor)"
            strokeWidth={strokeK}
            opacity={0.5}
          >
            <title>{e.viaQuery}</title>
          </line>
        )
      })}
    </>
  )
})
